/**
 * Languages Amazon Nova Sonic speaks: English, Hindi, Spanish, French, German, Italian and Portuguese
 * (mirrors server/services/nova-sonic-voices.ts). Tamil, Telugu, Bengali and the other regional Indian
 * languages are not supported by the model, so they are hidden when Nova Sonic is the voice engine.
 */
const NOVA_SONIC_BASE_LANGUAGES = new Set(["en", "hi", "es", "fr", "de", "it", "pt"]);

/** True for Nova Sonic languages. A missing language counts as English. */
export function isNovaSonicLanguage(code?: string | null): boolean {
  const base = (code ?? "").trim().toLowerCase().split("-")[0];
  return !base || NOVA_SONIC_BASE_LANGUAGES.has(base);
}

/**
 * Keeps the Nova Sonic languages in `codes`, plus any in `alsoKeep` (for example the language an existing
 * agent already uses, so an open agent never loses its own value from a dropdown).
 */
export function filterToNovaLanguages(codes: string[], alsoKeep: Array<string | null | undefined> = []): string[] {
  const keep = new Set(alsoKeep.filter((c): c is string => !!c).map((c) => c.toLowerCase()));
  return codes.filter((code) => isNovaSonicLanguage(code) || keep.has(code.toLowerCase()));
}

/**
 * The languages an agent form offers: only those with at least one voice in My Voices, plus any in `alsoKeep`
 * (the agent's own language, so opening an older agent never blanks the dropdown). When nothing is saved yet the
 * whole list is returned instead, so the form is never an empty dead end; the voice section tells the person to
 * add voices. A list without `savedCount` (an older server) is returned as it is.
 */
export function languagesWithSavedVoices<T extends { code: string; savedCount?: number }>(
  languages: T[],
  alsoKeep: Array<string | null | undefined> = [],
): T[] {
  if (!languages.some((l) => (l.savedCount ?? 0) > 0)) return languages;
  const keep = new Set(alsoKeep.filter((c): c is string => !!c).map((c) => c.toLowerCase()));
  return languages.filter((l) => (l.savedCount ?? 0) > 0 || keep.has(l.code.toLowerCase()));
}
